import React, { useEffect, useRef, useState, useCallback } from 'react';
import { B } from '../../constants';
import { borderRadius, shadows, transitions, animationDurations } from '../../designTokens';

const variantColors = {
  success: { bg: B.success || '#22c55e', icon: '✅', color: '#fff' },
  error:   { bg: B.error   || '#ef4444', icon: '❌', color: '#fff' },
  warning: { bg: B.warning || '#f59e0b', icon: '⚠️', color: '#fff' },
  info:    { bg: B.info    || '#0ea5e9', icon: 'ℹ️', color: '#fff' },
};

const positionStyles = {
  'top-right':     { top: 24, right: 24, flexDirection: 'column', alignItems: 'flex-end' },
  'top-left':      { top: 24, left: 24, flexDirection: 'column', alignItems: 'flex-start' },
  'top-center':    { top: 24, left: '50%', transform: 'translateX(-50%)', flexDirection: 'column', alignItems: 'center' },
  'bottom-right':  { bottom: 24, right: 24, flexDirection: 'column-reverse', alignItems: 'flex-end' },
  'bottom-left':   { bottom: 24, left: 24, flexDirection: 'column-reverse', alignItems: 'flex-start' },
  'bottom-center': { bottom: 24, left: '50%', transform: 'translateX(-50%)', flexDirection: 'column-reverse', alignItems: 'center' },
};

const sizeMap = {
  sm: { padding: '10px 16px', fontSize: 13, maxWidth: 320 },
  md: { padding: '14px 22px', fontSize: 14, maxWidth: 380 },
  lg: { padding: '18px 28px', fontSize: 16, maxWidth: 480 },
};

const animationKeyframes = {
  fade:  `@keyframes toast-fade{from{opacity:0}to{opacity:1}}`,
  slide: `@keyframes toast-slide{from{opacity:0;transform:translateY(20px)}to{opacity:1;transform:translateY(0)}}`,
  scale: `@keyframes toast-scale{from{opacity:0;transform:scale(0.9)}to{opacity:1;transform:scale(1)}}`,
};

let keyframesInjected = false;
function ensureToastKeyframes() {
  if (keyframesInjected) return;
  const style = document.createElement('style');
  style.textContent = Object.values(animationKeyframes).join(' ');
  document.head.appendChild(style);
  keyframesInjected = true;
}

const priorityLevels = { low: 0, normal: 1, high: 2, critical: 3 };

function ToastIcon({ variant, icon, size }) {
  if (icon === null) return null;
  if (icon !== undefined) return <span style={{ flexShrink: 0, fontSize: size === 'sm' ? 18 : size === 'lg' ? 26 : 22, lineHeight: 1 }}>{icon}</span>;
  const v = variantColors[variant] || variantColors.success;
  return <span style={{ flexShrink: 0, fontSize: size === 'sm' ? 18 : size === 'lg' ? 26 : 22, lineHeight: 1 }}>{v.icon}</span>;
}

function ToastAction({ action, onActionClick, size, textColor }) {
  if (!action) return null;
  return (
    <button
      onClick={(e) => { e.stopPropagation(); onActionClick(); }}
      aria-label={action.label}
      style={{
        background: 'rgba(255,255,255,0.15)',
        border: '1px solid rgba(255,255,255,0.3)',
        borderRadius: 6,
        color: textColor || '#fff',
        cursor: 'pointer',
        fontFamily: "'Montserrat',sans-serif",
        fontSize: size === 'sm' ? 11 : size === 'lg' ? 14 : 12,
        fontWeight: 600,
        padding: '4px 12px',
        transition: `all ${transitions.fast}`,
        whiteSpace: 'nowrap',
        flexShrink: 0,
      }}
      onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.25)'; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; }}
    >
      {action.label}
    </button>
  );
}

function ToastProgressBar({ show, progress, progressColor, position: progPosition, borderRadius: progRadius, reducedMotion }) {
  if (!show) return null;
  return (
    <div style={{
      position: 'absolute',
      [progPosition === 'top' ? 'top' : 'bottom']: 0,
      left: 0, right: 0, height: 3,
      background: 'rgba(255,255,255,0.2)',
      borderRadius: progPosition === 'top' ? `${progRadius} ${progRadius} 0 0` : `0 0 ${progRadius} ${progRadius}`,
      overflow: 'hidden',
    }}>
      <div style={{
        height: '100%', width: `${progress}%`,
        background: progressColor || 'rgba(255,255,255,0.6)',
        transition: reducedMotion ? 'none' : 'width 0.1s linear',
      }} />
    </div>
  );
}

function ToastCloseButton({ show, onClick, closeIcon, size, textColor }) {
  if (!show) return null;
  return (
    <button
      onClick={(e) => { e.stopPropagation(); onClick('manual'); }}
      aria-label="Dismiss"
      style={{
        background: 'rgba(255,255,255,0.15)',
        border: 'none', borderRadius: '50%',
        width: size === 'sm' ? 20 : size === 'lg' ? 28 : 24,
        height: size === 'sm' ? 20 : size === 'lg' ? 28 : 24,
        cursor: 'pointer',
        color: textColor || '#fff',
        fontSize: size === 'sm' ? 10 : size === 'lg' ? 14 : 12,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexShrink: 0,
        transition: `all ${transitions.fast}`,
      }}
      onMouseEnter={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.3)'; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'rgba(255,255,255,0.15)'; }}
    >
      {closeIcon || '✕'}
    </button>
  );
}

export default function Toast({
  message,
  title,
  description,
  type = 'success',
  duration = 3200,
  onClose,
  position = 'bottom-right',
  action,
  closeAfterAction = true,
  icon,
  showProgress = true,
  progressColor,
  animation = 'fade',
  pauseOnHover = true,
  radius = 'md',
  size = 'md',
  transitionDuration: tDuration = 'normal',
  stack = true,
  index = 0,
  stackSpacing = 12,
  style,
  contentStyle,
  showCloseButton = true,
  autoDismiss = true,
  onDismiss,
  persistent = false,
  priority = 'normal',
  closeIcon,
  progressPosition = 'bottom',
  keyboardDismiss = true,
  autoFocus = false,
  rtl,
  sound,
  customVariant,
}) {
  const [progress, setProgress] = useState(100);
  const [paused, setPaused] = useState(false);
  const [exiting, setExiting] = useState(false);
  const toastRef = useRef(null);
  const rafRef = useRef(null);
  const startRef = useRef(Date.now());
  const elapsedRef = useRef(0);
  const [reducedMotion, setReducedMotion] = useState(false);

  const handleDismiss = useCallback((reason) => {
    setExiting(true);
    let t = tDuration === 'fast' ? 150 : tDuration === 'slow' ? 300 : 200;
    if (reducedMotion) t = 0;
    setTimeout(() => {
      if (onDismiss) onDismiss(reason);
      if (onClose) onClose();
    }, reducedMotion ? 0 : t);
  }, [onDismiss, onClose, tDuration, reducedMotion]);

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (!mq) return;
    setReducedMotion(mq.matches);
    const handler = (e) => setReducedMotion(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);

  useEffect(() => {
    ensureToastKeyframes();
  }, []);

  useEffect(() => {
    if (persistent || !autoDismiss) return;
    const tick = () => {
      const elapsed = elapsedRef.current + (Date.now() - startRef.current);
      const remaining = Math.max(0, 100 - (elapsed / duration) * 100);
      setProgress(remaining);
      if (remaining <= 0) {
        handleDismiss('auto');
      } else if (typeof window.requestAnimationFrame === 'function') {
        rafRef.current = window.requestAnimationFrame(tick);
      }
    };
    if (!paused && typeof window.requestAnimationFrame === 'function') {
      startRef.current = Date.now();
      rafRef.current = window.requestAnimationFrame(tick);
    }
    return () => { if (rafRef.current && typeof window.cancelAnimationFrame === 'function') window.cancelAnimationFrame(rafRef.current); };
  }, [duration, paused, persistent, autoDismiss, handleDismiss]);

  useEffect(() => {
    if (keyboardDismiss) {
      const handler = (e) => {
        if (e.key === 'Escape') handleDismiss('manual');
      };
      window.addEventListener('keydown', handler);
      return () => window.removeEventListener('keydown', handler);
    }
  }, [keyboardDismiss, handleDismiss]);

  useEffect(() => {
    if (autoFocus && toastRef.current) {
      toastRef.current.focus();
    }
  }, [autoFocus]);

  useEffect(() => {
    if (sound) {
      try {
        const src = typeof sound === 'string' ? sound : '/sounds/toast.mp3';
        const audio = new Audio(src);
        audio.volume = 0.3;
        audio.play().catch(() => {});
      } catch (e) {}
    }
  }, [sound]);

  const handleActionClick = useCallback(() => {
    if (action && action.onClick) action.onClick();
    if (closeAfterAction) handleDismiss('action');
  }, [action, closeAfterAction, handleDismiss]);

  const handleMouseEnter = useCallback(() => {
    if (pauseOnHover) {
      elapsedRef.current += Date.now() - startRef.current;
      setPaused(true);
    }
  }, [pauseOnHover]);

  const handleMouseLeave = useCallback(() => {
    if (pauseOnHover) {
      startRef.current = Date.now();
      setPaused(false);
    }
  }, [pauseOnHover]);

  const variant = customVariant || variantColors[type] || variantColors.success;
  const sz = sizeMap[size] || sizeMap.md;
  const rad = borderRadius[radius] || borderRadius.md;

  let animName = 'toast-fade';
  if (animation === 'slide') animName = 'toast-slide';
  else if (animation === 'scale') animName = 'toast-scale';

  const animDuration = reducedMotion ? '0s' : (tDuration === 'fast' ? animationDurations.fadeIn : tDuration === 'slow' ? animationDurations.slideUp : animationDurations.scaleIn);

  let offsetY = 0;
  if (stack && index > 0) {
    offsetY = index * (parseInt(sz.padding) * 2 + 60 + stackSpacing);
    if (position.includes('bottom')) offsetY = -offsetY;
  }

  const containerStyle = {
    position: 'fixed',
    zIndex: 9999 + (priorityLevels[priority] || 0),
    ...positionStyles[position] || positionStyles['bottom-right'],
    pointerEvents: 'none',
  };

  const toastStyle = {
    background: variant.bg,
    color: variant.color || '#fff',
    padding: sz.padding,
    fontFamily: "'Montserrat',sans-serif",
    fontSize: sz.fontSize,
    fontWeight: 600,
    boxShadow: shadows.xl,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
    maxWidth: sz.maxWidth,
    minWidth: 280,
    overflow: 'hidden',
    position: 'relative',
    borderRadius: rad,
    pointerEvents: 'auto',
    transition: reducedMotion ? 'none' : `all ${transitions.normal}`,
    transform: exiting
      ? `translateY(${animation === 'none' ? 0 : 10}px) scale(${animation === 'scale' ? 0.95 : 1})`
      : offsetY ? `translateY(${offsetY}px)` : 'none',
    opacity: exiting ? 0 : 1,
    ...(rtl ? { direction: 'rtl' } : {}),
    ...(style || {}),
  };

  const contentRow = {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 10,
    ...(contentStyle || {}),
  };

  const textBlock = {
    flex: 1,
    minWidth: 0,
  };

  const titleStyle = {
    fontWeight: 700,
    fontSize: size === 'sm' ? 13 : size === 'lg' ? 17 : 15,
    lineHeight: 1.3,
    marginBottom: 2,
  };

  const messageStyle = {
    fontWeight: size === 'lg' ? 500 : 600,
    lineHeight: 1.4,
    wordBreak: 'break-word',
  };

  const descriptionStyle = {
    fontWeight: 400,
    fontSize: size === 'sm' ? 11 : size === 'lg' ? 14 : 13,
    opacity: 0.9,
    lineHeight: 1.4,
    marginTop: 2,
  };

  return (
    <div style={containerStyle}>
      <style>{Object.values(animationKeyframes).join(' ')}</style>
      <div
        ref={toastRef}
        role="alert"
        tabIndex={autoFocus ? -1 : undefined}
        aria-live="assertive"
        aria-atomic="true"
        style={{
          ...toastStyle,
          animation: animation !== 'none' && !reducedMotion && !exiting
            ? `${animName} ${animDuration} ease`
            : 'none',
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
      >
        <div style={contentRow}>
          <ToastIcon variant={type} icon={icon} size={size} />
          <div style={textBlock}>
            {title && <div style={titleStyle}>{title}</div>}
            {message && <div style={messageStyle}>{message}</div>}
            {description && <div style={descriptionStyle}>{description}</div>}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexShrink: 0 }}>
            <ToastAction action={action} onActionClick={handleActionClick} size={size} textColor={variant.color} />
            <ToastCloseButton show={showCloseButton} onClick={handleDismiss} closeIcon={closeIcon} size={size} textColor={variant.color} />
          </div>
        </div>
        <ToastProgressBar
          show={showProgress}
          progress={progress}
          progressColor={progressColor}
          position={progressPosition}
          borderRadius={rad}
          reducedMotion={reducedMotion}
        />
      </div>
    </div>
  );
}
